import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserData } from '../../reducers/user.reducer';
import { setIsLoading, setToastMessage } from "../../reducers/common.reducer";
import { getUserfundService, getHistoryService } from '../../services/user.service';
import { FiClock, FiX } from 'react-icons/fi';

const Wallet = () => {
    const [userFund, setUserFund] = useState([]);
    const dispatch = useDispatch();
    const userData = useSelector(selectUserData);
    const [showModal, setShowModal] = useState(false);
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const [startDate, setStartDate] = useState(sevenDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
    const [history, setHistory] = useState([]);

    // Get initial data from Redux or LocalStorage
    const userId = userData?.userInfo?.userId || JSON.parse(localStorage.getItem("USER"))?.userId;

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        if (userId) {
            const timer = setTimeout(() => {
                getUserFund(signal, userId);
            }, 500);

            return () => {
                controller.abort();
                clearTimeout(timer);
            };
        }
    }, [userId]);

    const getUserFund = async (signal, id) => {
        dispatch(setIsLoading(true));
        try {
            const result = await getUserfundService(signal, id);
            if (result.data) {
                setUserFund(result.data);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                dispatch(setToastMessage({
                    message: error?.response?.data?.message || error?.message,
                    type: "error",
                }));
            }
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const getHistory = async (signal, startDate, endDate, userId) => {
        dispatch(setIsLoading(true));
        try {
            const result = await getHistoryService(signal, startDate, endDate, userId);
            if (result.data) {
                setHistory(result.data);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                dispatch(setToastMessage({
                    message: error?.response?.data?.message || error?.message,
                    type: "error",
                }));
            }
        } finally {
            dispatch(setIsLoading(false));
        }
    }

    const totalAmount = userFund?.reduce((acc, fund) => acc + Number(fund.amount), 0);

    return (
        <div className='w-full h-[90vh] overflow-y-auto bg-slate-50 font-sans'>
            <div className='max-w-md mx-auto px-4 py-4'>

                {/* Balance Card */}
                <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl px-6 py-4 mb-2 text-white relative overflow-hidden'>
                    <div className="relative flex justify-between items-end z-10">
                        <div className='flex flex-col items-start'>
                            <div className='flex items-center gap-2'>
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>
                                </div>
                                <p className='text-blue-100 text-sm font-medium mb-1 opacity-80'>Current Balance</p>
                            </div>
                            <h2 className='text-3xl font-bold tracking-tight'>
                                {totalAmount?.toLocaleString()} <span className="text-lg font-normal opacity-90">Ks</span>
                            </h2>
                        </div>
                        <div className='text-slate-200 text-sm font-medium px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer'
                            onClick={() => setShowModal(true)}
                        >
                            <FiClock className='w-4 h-4' />
                            <span className='underline'>History</span>
                        </div>
                    </div>
                    {/* Decorative Circles */}
                    <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white opacity-10 rounded-full"></div>
                    <div className="absolute right-12 -top-4 w-20 h-20 bg-white opacity-5 rounded-full"></div>
                </div>

                {/* Transaction Details */}
                <div className='bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden'>
                    <div className="px-6 py-4 border-b border-slate-50 flex justify-between items-center">
                        <h3 className='font-semibold text-slate-700'>Bank Type</h3>
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full">{userFund.length} types</span>
                    </div>

                    <div className='divide-y divide-slate-50'>
                        {userFund.length > 0 ? (
                            userFund.map((fund) => (
                                <div key={fund.transactionTypeId} className='flex justify-between items-center px-6 py-5 hover:bg-slate-50 transition-all duration-200'>
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-xl ${Number(fund.amount) < 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className={`w-5 h-5 ${Number(fund.amount) < 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                                            >
                                                <rect x="2" y="5" width="20" height="14" rx="2" />
                                                <line x1="2" y1="10" x2="22" y2="10" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className='text-sm font-bold text-slate-800'>{fund.transactionType}</p>
                                            {/* <p className='text-xs text-slate-400 font-medium'>Success</p> */}
                                        </div>
                                    </div>
                                    <div className='text-right'>
                                        <p className={`font-bold ${Number(fund.amount) < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                            {Number(fund.amount) < 0 ? '' : '+'}{Number(fund.amount).toLocaleString()}
                                        </p>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">MMK</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-20 text-center">
                                <p className="text-slate-400 italic">No transactions found.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showModal && (
                <div
                    className={`fixed inset-0 z-50 flex justify-end transition-all duration-300 
                        ${showModal ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                    <div
                        className={`absolute inset-0 bg-black transition-opacity duration-300 
                            ${showModal ? 'opacity-50' : 'opacity-0'}`}
                        onClick={() => setShowModal(false)}
                    />
                    <div
                        className={`relative h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-out 
                            ${showModal ? 'translate-x-0' : 'translate-x-full'}`}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-slate-700">
                                Transaction History
                            </h3>
                            <button
                                type="button"
                                className="p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                                onClick={() => setShowModal(false)}
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>
                        <div>
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onChange={(e) => setStartDate(e.target.value)}
                                    value={startDate}
                                />
                                <span className="text-slate-400">to</span>
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onChange={(e) => setEndDate(e.target.value)}
                                    value={endDate}
                                />
                                <button
                                    onClick={() => getHistory(new AbortController().signal, startDate, endDate, userId)}
                                    className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                >
                                    Filter
                                </button>
                            </div>
                        </div>
                        <div className="h-full overflow-y-auto">
                            <div className="divide-y divide-slate-50">
                                {history.length > 0 ? (
                                    history.map((history, index) => (
                                        <div
                                            key={index}
                                            className="flex flex-col items-center px-6 py-2 hover:bg-slate-200 transition-all duration-200 border-b border-slate-100 m-1"
                                        >
                                            <div className="w-full flex justify-between items-center">
                                                <div className="flex items-center gap-4">
                                                    <div className={`${history?.type === 'inward' || history?.type === 'income' || history?.type === 'user-to-user' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'} w-8 h-8 rounded-full flex items-center justify-center font-bold`}>
                                                        {history?.type === 'inward' && '+'}
                                                        {history?.type === 'outward' && '-'}
                                                        {history?.type === 'income' && '+'}
                                                        {history?.type === 'expense' && '-'}
                                                        {history?.type === 'user-to-user' && '='}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-800">
                                                            {history?.type?.toUpperCase()}
                                                        </p>
                                                        <p className="text-xs text-slate-400 font-medium">
                                                            {new Date(history?.transferDate?.value || history?.createdAt?.value || Date.now()).toLocaleString()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`${history?.type === 'inward' || history?.type === 'income' || history?.type === 'user-to-user' ? 'text-emerald-600' : 'text-rose-600'} font-bold`}>
                                                        {history?.type === 'inward' ? '+' : '-'}{Number(history?.amount || 0).toLocaleString()}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{history?.transactionType}</p>
                                                </div>
                                            </div>

                                            <div className='w-full px-6 py-4'>
                                                {history?.type === 'inward' && (
                                                    <p className="text-xs text-slate-400 font-medium">
                                                        {history?.agentName || ''} → {history?.username || ''}
                                                    </p>
                                                )}
                                                {history?.type === 'outward' && (
                                                    <p className="text-xs text-slate-400 font-medium">
                                                        {history?.username || ''} → {history?.agentName || ''}
                                                    </p>
                                                )}
                                                {history?.type === 'user-to-user' && (
                                                    <p className="text-xs text-slate-400 font-medium">
                                                        {history?.sender || ''} → {history?.recipient || ''}
                                                    </p>
                                                )}
                                                <p className="text-xs text-slate-400 font-medium">
                                                    {history?.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-10 text-center">
                                        <p className="text-slate-400 italic text-sm">
                                            No transactions found.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Wallet;